import { type ReactNode, useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { DocumentCreateDialog } from "@/features/document-create/components/DocumentCreateDialog";
import {
  type DocumentCreateKind,
  DocumentCreateLauncherContext,
} from "@/lib/document-create-launcher";

/**
 * Mounts one `DocumentCreateDialog` for the protected shell and exposes the
 * launcher through `DocumentCreateLauncherContext`. Any route change closes
 * the dialog: a success screen's "View" link and the "Open full page" action
 * both navigate, and the dialog must not linger over the destination.
 */
export function DocumentCreateLauncherProvider({ children }: { children: ReactNode }) {
  const { key: locationKey } = useLocation();
  const [kind, setKind] = useState<DocumentCreateKind | null>(null);
  const [open, setOpen] = useState(false);
  // Keys the dialog so every launch mounts a fresh instance: no draft, confirm,
  // or created-document state can carry over from an earlier session.
  const [launchId, setLaunchId] = useState(0);

  const launcher = useMemo(
    () => ({
      open: (nextKind: DocumentCreateKind) => {
        setKind(nextKind);
        setLaunchId((id) => id + 1);
        setOpen(true);
      },
    }),
    [],
  );

  const handleOpenChange = useCallback((next: boolean) => setOpen(next), []);

  const [previousLocationKey, setPreviousLocationKey] = useState(locationKey);
  if (previousLocationKey !== locationKey) {
    setPreviousLocationKey(locationKey);
    setOpen(false);
  }

  return (
    <DocumentCreateLauncherContext.Provider value={launcher}>
      {children}
      {kind ? (
        <DocumentCreateDialog
          key={launchId}
          kind={kind}
          onOpenChange={handleOpenChange}
          open={open}
        />
      ) : null}
    </DocumentCreateLauncherContext.Provider>
  );
}
